import { HEALTHIE_SYSTEM_URL } from '@bariendo/lib/constants';
import type { DiagnosticReport, Observation, Reference } from '@medplum/fhirtypes';

type ResultRef = Reference<Observation> & { resource?: Observation };

export function isHealthieDiagnosticReport(report: DiagnosticReport): boolean {
  return report.identifier?.some((id) => id.system === HEALTHIE_SYSTEM_URL) ?? false;
}

/** GraphQL `... on Observation { hasMember }` may omit `resourceType`; only `hasMember` is queried. */
function graphqlObservationPanelHasMembers(resource: unknown): boolean {
  if (resource == null || typeof resource !== 'object') {
    return false;
  }
  const hasMember = (resource as { hasMember?: unknown }).hasMember;
  return Array.isArray(hasMember) && hasMember.length > 0;
}

/**
 * Healthie lab panels expose stub Observations in `result` until real values exist; those lack `hasMember`.
 * Requires `result[].resource` from GraphQL; if not expanded, returns true so we do not hide data incorrectly.
 */
export function healthieDiagnosticReportHasTimelineResults(report: DiagnosticReport): boolean {
  const results = report.result ?? [];
  if (results.length === 0) {
    return false;
  }

  let anyResultNotExpanded = false;
  let anyPanelHasMembers = false;

  for (const ref of results) {
    const resource = (ref as ResultRef).resource as unknown;
    if (resource == null) {
      anyResultNotExpanded = true;
      continue;
    }
    if (graphqlObservationPanelHasMembers(resource)) {
      anyPanelHasMembers = true;
    }
  }

  if (anyResultNotExpanded) {
    return true;
  }

  return anyPanelHasMembers;
}

export function shouldShowHealthieDiagnosticReportInTimeline(report: DiagnosticReport): boolean {
  if (!isHealthieDiagnosticReport(report)) {
    return true;
  }
  return healthieDiagnosticReportHasTimelineResults(report);
}
